import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';

import { searchSubscriptionFeatureUpsellCaptions } from './helpers';
import { getAsset } from '../../../../../../../util/assets';
import useFeaturePermission from '../../../../../../pro/services/featurePermissions';
import Button from '../../../../../../../components/global/Button';
import { getErrorString } from '../../../../../../../util/normalize-errors';
import useOpenAlert from '../../../../../../../hooks/useOpenAlert';
import { STRIPE_CHECKOUT_MODAL } from '../../../../../../../components/global/StripeCheckout';
import { formatPriceForDisplay } from '../../../../../../../util/format-money';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../../../../constants/account-referrals';
import useOpenModal from '../../../../../../../hooks/useOpenModal';
import Spinner from '../../../../../../../components/global/Spinner';
import ArrowRight from '../../../../../../../components/icons/ArrowRight';
import PhotoSearchUpsellModal from '../../modal';
import {
  isEligibleForShutterbugFreeTrial,
  SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD,
} from '../../../PhotoSearchCheckout/helpers';
import { AccountPhotoSearchCheckout } from '../../../../../../../graphql/queries/account';
import usePhotoSearchCheckout from '../../../PhotoSearchCheckout/hooks/usePhotoSearchCheckout';
import { SHUTTERBUG_TRIAL_DAYS_FALLBACK } from '../../helpers';

const propTypes = {
  featureId: PropTypes.string,
  onCheckoutSuccess: PropTypes.func,
  onClose: PropTypes.func,
  loadingComponent: PropTypes.node,
  banner: PropTypes.bool,
  className: PropTypes.string,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  illustration: PropTypes.node,
  account: PropTypes.shape({
    id: PropTypes.string,
  }),
  refetchAccountPhotoSearchCheckout: PropTypes.func.isRequired,
};

const SearchSubscriptionFeatureUpsell = ({
  featureId,
  title,
  subtitle,
  illustration,
  onCheckoutSuccess,
  onClose,
  banner,
  className,
  loadingComponent = (
    <div className="d-flex justify-content-center p-5">
      <Spinner />
    </div>
  ),
  account,
  refetchAccountPhotoSearchCheckout,
}) => {
  const openAlert = useOpenAlert();
  const openModal = useOpenModal();

  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const purchasedEventFeatures = useFeaturePermission({
    features: featureId ? [featureId] : undefined,
  });

  const { onCheckout, submitting } = usePhotoSearchCheckout({
    account,
    onCheckoutError: error => {
      openAlert({
        message: getErrorString(error),
        info: error?.request_id || '',
      });
    },
    onCheckoutSuccess: async () => {
      refetchAccountPhotoSearchCheckout();
      onCheckoutSuccess?.();

      openAlert({
        type: 'success',
        title: 'Congratulations!',
        message:
          'Your plan has been upgraded! You can now enjoy newly unlocked features!',
      });

      onClose?.();
    },
    searchSubscriptionItems: purchasedEventFeatures?.[featureId]?.plans ?? [],
  });

  const monthlyPlan = (purchasedEventFeatures[featureId]?.plans ?? []).find(
    plan =>
      plan.billing_period === SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD.MONTHLY,
  );
  const yearlyPlan = (purchasedEventFeatures[featureId]?.plans ?? []).find(
    plan =>
      plan.billing_period === SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD.YEARLY,
  );

  const onUpgrade = ({ searchSubscriptionItem }) => {
    openModal(STRIPE_CHECKOUT_MODAL, {
      freeButtonLabel: isEligibleForShutterbugFreeTrial(account)
        ? `${
            searchSubscriptionItem?.trial_days ?? SHUTTERBUG_TRIAL_DAYS_FALLBACK
          }-day free trial`
        : undefined,
      buttonLabel: `Pay $${formatPriceForDisplay(
        searchSubscriptionItem?.price.value,
      )}`,

      checkoutPriceVariables: ({ promo_code: promoCode }) => {
        return {
          search_subscription_items: [
            {
              search_subscription_item_id: searchSubscriptionItem?.id,
            },
          ],
          promo_code: promoCode,
        };
      },
      accountReferralTypes: [
        ACCOUNT_REFERRAL_TYPE.SHUTTERBUG_PROMO,
        ACCOUNT_REFERRAL_TYPE.TIER_1_SHUTTERBUG_PROMO,
      ],
      onSuccess: token =>
        onCheckout({
          searchSubscriptionItem,
          token,
        }),
    });
  };

  const classes = classNames({
    'search-subscription-feature-upsell-banner': banner,
    'search-subscription-feature-upsell-container': !banner,
    'search-subscription-feature-upsell-container-with-illustration':
      !banner &&
      !!searchSubscriptionFeatureUpsellCaptions[featureId]?.illustration,
    [className]: !!className,
  });

  if (purchasedEventFeatures.loading) {
    return <>{loadingComponent}</>;
  }

  const upgradeModal = (
    <PhotoSearchUpsellModal
      onClose={() => {
        onClose?.();
        setUpgradeModalOpen(false);
      }}
      forceOpen={upgradeModalOpen}
      onCheckoutSuccess={() => {
        onCheckoutSuccess?.();
        onClose?.();
      }}
    />
  );

  if (banner) {
    return (
      <>
        <Button btnStyle="reset" className={classes} onClick={onUpgrade}>
          <img
            src={getAsset('/diamond-orange.svg')}
            alt="Unlock your potential"
            width="28"
            className="mr-1"
          />
          <h3>
            {title ?? searchSubscriptionFeatureUpsellCaptions[featureId]?.title}
          </h3>
          <ArrowRight className="ml-1" color="#fff" />
        </Button>
        {upgradeModal}
      </>
    );
  }

  return (
    <div className={classes}>
      {(illustration ??
        searchSubscriptionFeatureUpsellCaptions[featureId]?.illustration) && (
        <img
          alt="Unlock feature"
          src={getAsset(
            illustration ??
              searchSubscriptionFeatureUpsellCaptions[featureId]?.illustration,
          )}
        />
      )}
      <div className="search-subscription-feature-upsell-content">
        <img
          src={getAsset('/diamond-orange.svg')}
          alt="Unlock your potential"
          width="37"
          className="mb-2"
        />
        <h2>
          {title ?? searchSubscriptionFeatureUpsellCaptions[featureId]?.title}
        </h2>
        {subtitle ??
          searchSubscriptionFeatureUpsellCaptions[featureId]?.subtitle}

        {(!featureId || purchasedEventFeatures?.id) && (
          <>
            <div className="mt-5">
              <div className="search-subscription-feature-checkout-wrapper">
                <Button
                  onClick={() =>
                    onUpgrade({ searchSubscriptionItem: yearlyPlan })
                  }
                  disabled={submitting}
                  btnStyle="yellow"
                  btnSize="lg"
                >
                  {submitting ? (
                    'Loading...'
                  ) : (
                    <>
                      {isEligibleForShutterbugFreeTrial(account) ? (
                        <div>
                          <p className="mb-0 text-sm">
                            Start{' '}
                            {yearlyPlan?.trial_days ??
                              SHUTTERBUG_TRIAL_DAYS_FALLBACK}
                            -day free trial
                          </p>
                          <p className="text-xs mb-0 text-normal">
                            ${formatPriceForDisplay(yearlyPlan?.price?.value)} /
                            year after trial period
                          </p>
                        </div>
                      ) : (
                        <div>
                          <p className="mb-0 text-sm">Waldo Plus Annual</p>
                          <p className="text-xs mb-0 text-normal">
                            ${formatPriceForDisplay(yearlyPlan?.price?.value)} /
                            year
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </Button>
                <Button
                  onClick={() =>
                    onUpgrade({ searchSubscriptionItem: monthlyPlan })
                  }
                  disabled={submitting}
                  btnStyle="glass"
                  btnSize="lg"
                >
                  {submitting ? (
                    'Loading...'
                  ) : (
                    <div>
                      <p className="mb-0 text-sm">Waldo Plus Monthly</p>
                      <p className="text-xs mb-0 text-normal">
                        ${formatPriceForDisplay(monthlyPlan?.price?.value)} /
                        month
                      </p>
                    </div>
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {upgradeModal}
    </div>
  );
};

SearchSubscriptionFeatureUpsell.propTypes = propTypes;

const withAccount = graphql(AccountPhotoSearchCheckout, {
  props: ({
    data: { me, refetch: refetchAccountPhotoSearchCheckout } = {},
  }) => ({
    account: me,
    refetchAccountPhotoSearchCheckout,
  }),
});

export default compose(withAccount)(SearchSubscriptionFeatureUpsell);
